import React, { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  ChevronRight,
  ChevronLeft,
  AlertTriangle,
  Languages,
  Crown,
  Ticket,
} from "lucide-react";

import { translations } from "@/i18n";
import { landingExtras } from "@/i18n_extras";
import { API } from "@/lib/constants";

import SocialProofToast from "@/components/SocialProofToast";

// Shared
import { Header } from "@/components/shared/Header";
import { Footer } from "@/components/shared/Footer";
import { LiveCounter } from "@/components/shared/LiveCounter";

// Landing sections
import { Hero } from "@/components/landing/Hero";
import { Stats } from "@/components/landing/Stats";
import { HiredAt } from "@/components/landing/HiredAt";
import { BeforeAfter } from "@/components/landing/BeforeAfter";
import { HowItWorks } from "@/components/landing/HowItWorks";
import { Testimonials } from "@/components/landing/Testimonials";
import { WhySection } from "@/components/landing/WhySection";
import { FAQ } from "@/components/landing/FAQ";
import { FinalCTA } from "@/components/landing/FinalCTA";

// Wizard
import { Stepper } from "@/components/wizard/Stepper";
import { Step1Upload } from "@/components/wizard/Step1Upload";
import { Step2Job } from "@/components/wizard/Step2Job";
import { Step3Profile } from "@/components/wizard/Step3Profile";
import { Step4Processing } from "@/components/wizard/Step4Processing";

// Results
import { ResultsScreen } from "@/components/results/ResultsScreen";

// 88 Days feature
import { VisaDaysPage } from "@/components/visadays/VisaDaysPage";

// Auth
import { useAuth } from "@/hooks/useAuth";
import { useQuota } from "@/hooks/useQuota";
import AuthCallback from "@/components/auth/AuthCallback";
import RedeemPromoModal from "@/components/auth/RedeemPromoModal";
import AdminDashboard from "@/components/admin/AdminDashboard";
import GuideWAWrapper from "@/pages/GuideWAWrapper";
import { GuideWACard } from "@/components/landing/GuideWACard";
import { FloatingGuideButton } from "@/components/shared/FloatingGuideButton";
import { MyCVsPage } from "@/components/user/MyCVsPage";
import { SupportWidget } from "@/components/shared/SupportWidget";
import UpgradeModal from "@/components/payments/UpgradeModal";
import PaymentSuccessPage from "@/components/payments/PaymentSuccessPage";

function App() {
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "en";
    try {
      const saved = localStorage.getItem("aussiecv_lang");
      if (saved && ["en", "fr", "de", "it", "es", "pt"].includes(saved)) return saved;
      const nav = (navigator.language || navigator.userLanguage || "en").toLowerCase();
      const short = nav.split("-")[0];
      if (["fr", "de", "it", "es", "pt"].includes(short)) return short;
    } catch (_e) { /* ignore */ }
    return "en";
  });
  // Persist language choice
  useEffect(() => {
    try { localStorage.setItem("aussiecv_lang", lang); } catch (_e) { /* ignore */ }
  }, [lang]);
  const t = translations[lang];
  const te = landingExtras[lang] || landingExtras.en;

  const [mode, setMode] = useState(() => {
    if (typeof window !== "undefined") {
      const path = window.location.pathname;
      if (path === "/admin") return "admin";
      if (path === "/payment/success") return "payment_success";
      if (path === "/payment/cancel") return "payment_cancel";
      if (path === "/guide-wa") return "guide_wa";
    }
    return "landing";
  });
  const [step, setStep] = useState(1);

  // Auth: optional Google sign-in via Emergent OAuth
  const { user, login, logout, refresh: refreshAuth } = useAuth();
  const { quota, refresh: refreshQuota } = useQuota(user);
  const [handlingCallback, setHandlingCallback] = useState(
    typeof window !== "undefined" && window.location.hash?.includes("session_id=")
  );

  // Modals
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [quotaBlockOpen, setQuotaBlockOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  // Keep URL in sync with mode (for admin deep-link + guide-wa)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const path = window.location.pathname;
    if (mode === "admin" && path !== "/admin") {
      window.history.replaceState({}, "", "/admin");
    } else if (mode === "guide_wa" && path !== "/guide-wa") {
      window.history.replaceState({}, "", "/guide-wa");
    } else if (mode !== "admin" && mode !== "guide_wa" && (path === "/admin" || path === "/guide-wa")) {
      window.history.replaceState({}, "", "/");
    }
  }, [mode]);

  // Auto-redirect from /admin if user is not admin (after auth loads)
  useEffect(() => {
    if (mode === "admin" && user && user.is_admin === false) {
      setMode("landing");
    }
  }, [mode, user]);

  const [cvText, setCvText] = useState("");
  const [fileName, setFileName] = useState("");
  const [jobText, setJobText] = useState("");
  const [jobUrl, setJobUrl] = useState("");
  const [jobMeta, setJobMeta] = useState({});
  const [profile, setProfile] = useState({ visa: "417", location: "", sector: "mining", notes: "" });

  const [result, setResult] = useState(null);
  const [optimizing, setOptimizing] = useState(false);
  const [error, setError] = useState("");

  const startWizard = () => {
    setMode("wizard");
    setStep(1);
    setError("");
    setResult(null);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };

  const openVisaDays = () => {
    setMode("visadays");
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };

  const openMyCVs = () => {
    setMode("mycvs");
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };

  const restart = () => {
    setMode("landing");
    setStep(1);
    setResult(null);
    setCvText("");
    setFileName("");
    setJobText("");
    setJobUrl("");
    setJobMeta({});
    setProfile({ visa: "417", location: "", sector: "mining", notes: "" });
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };

  const canNext = () => {
    if (step === 1) return cvText.trim().length > 40;
    if (step === 2) return jobText.trim().length > 40;
    if (step === 3) return profile.visa && profile.location.trim() && profile.sector;
    return true;
  };

  const next = async () => {
    setError("");
    if (!canNext()) {
      if (step === 1) setError(t.errors.missing_cv);
      else if (step === 2) setError(t.errors.missing_job);
      else if (step === 3) setError(t.errors.missing_profile);
      return;
    }
    if (step < 3) {
      setStep(step + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    // step 3 → check quota then optimize
    if (user && quota && !quota.is_premium && !quota.is_admin && quota.cv_used >= quota.cv_limit) {
      setQuotaBlockOpen(true);
      return;
    }
    setStep(4);
    setOptimizing(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
    try {
      const res = await axios.post(`${API}/optimize`, {
        original_cv_text: cvText,
        job_ad_text: jobText,
        visa_status: profile.visa,
        location: profile.location,
        sector: profile.sector,
        language: lang,
        extra_notes: profile.notes,
      }, {
        timeout: 90000, // 90s (backend Claude timeout is 30s, but we allow buffer for full pipeline)
        withCredentials: true,
      });
      setResult(res.data);
      setMode("results");
      refreshQuota();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 402) {
        setQuotaBlockOpen(true);
        setStep(3);
      } else if (status === 504 || status === 524 || status === 502 || status === 503 || e.code === "ECONNABORTED") {
        // Cloudflare timeout / AI too slow / provider unavailable — friendlier message
        const msgByLang = {
          en: "The AI is taking longer than usual. Please try again — it usually works on the second try. If it persists, check your internet or contact support.",
          fr: "L'IA prend plus de temps que d'habitude. Réessaie — ça fonctionne généralement au 2e essai. Si le problème persiste, vérifie ta connexion ou contacte le support.",
          de: "Die KI braucht länger als üblich. Bitte versuche es erneut — meistens klappt es beim 2. Versuch.",
          it: "L'IA sta impiegando più tempo del solito. Riprova — di solito funziona al 2° tentativo.",
          es: "La IA está tardando más de lo habitual. Inténtalo de nuevo — normalmente funciona al 2º intento.",
          pt: "A IA está a demorar mais do que o habitual. Tenta novamente — geralmente funciona à 2ª tentativa.",
        };
        setError(msgByLang[lang] || msgByLang.en);
        setStep(3);
      } else {
        // Only accept "detail" if it looks like a real API error (short JSON string), not HTML
        const isHtml = typeof detail === "string" && (detail.includes("<!DOCTYPE") || detail.includes("<html") || detail.length > 500);
        setError(isHtml ? t.errors.optimize : (detail || t.errors.optimize));
        setStep(3);
      }
    } finally {
      setOptimizing(false);
    }
  };

  const back = () => {
    setError("");
    if (step > 1) setStep(step - 1);
    else setMode("landing");
  };

  // Labels for quota banner / block modal
  const QUOTA_LABELS = {
    en: { used: "CVs used", of: "of", upgrade: "You've used all your free CVs", upgrade_sub: "Redeem a promo code to unlock unlimited generations.", cta: "Redeem promo code", close: "Close" },
    fr: { used: "CV utilisés", of: "sur", upgrade: "Tu as utilisé tous tes CV gratuits", upgrade_sub: "Utilise un code promo pour débloquer des générations illimitées.", cta: "Utiliser un code promo", close: "Fermer" },
    de: { used: "CVs verbraucht", of: "von", upgrade: "Du hast alle kostenlosen CVs verwendet", upgrade_sub: "Löse einen Promo-Code ein, um unbegrenzte Generierungen freizuschalten.", cta: "Promo-Code einlösen", close: "Schließen" },
    it: { used: "CV usati", of: "su", upgrade: "Hai usato tutti i CV gratuiti", upgrade_sub: "Riscatta un codice promo per sbloccare generazioni illimitate.", cta: "Riscatta codice promo", close: "Chiudi" },
    es: { used: "CV usados", of: "de", upgrade: "Has usado todos tus CV gratuitos", upgrade_sub: "Canjea un código promo para desbloquear generaciones ilimitadas.", cta: "Canjear código promo", close: "Cerrar" },
    pt: { used: "CVs usados", of: "de", upgrade: "Usaste todos os teus CVs gratuitos", upgrade_sub: "Resgata um código promo para desbloquear gerações ilimitadas.", cta: "Resgatar código promo", close: "Fechar" },
  };
  const ql = QUOTA_LABELS[lang] || QUOTA_LABELS.en;

  // Admin mode: render dedicated console
  if (mode === "payment_success") {
    return (
      <PaymentSuccessPage
        lang={lang}
        onDone={() => {
          setMode("landing");
          window.history.replaceState({}, "", "/");
          refreshAuth();
          refreshQuota();
        }}
      />
    );
  }
  if (mode === "payment_cancel") {
    return (
      <PaymentSuccessPage
        lang={lang}
        onDone={() => {
          setMode("landing");
          window.history.replaceState({}, "", "/");
        }}
      />
    );
  }
  if (mode === "admin") {
    if (!user) {
      // Not signed in: show minimal gate
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#FAF6F0] p-6" data-testid="admin-gate">
          <div className="max-w-md w-full bg-white rounded-2xl border border-[#EAE5DB] p-8 text-center">
            <div className="font-heading font-bold text-xl text-[#292524] mb-2">Admin Dashboard</div>
            <p className="text-sm text-[#78716C] mb-5">You must be signed in as admin to access this page.</p>
            <button onClick={login} className="btn-primary w-full">Sign in with Google</button>
            <button onClick={() => setMode("landing")} className="text-xs text-[#78716C] hover:underline mt-4">← Back to site</button>
          </div>
        </div>
      );
    }
    if (user.is_admin === false) {
      return null; // useEffect will redirect to landing
    }
    return (
      <AdminDashboard
        user={user}
        onLogout={async () => { await logout(); setMode("landing"); }}
        onExit={() => setMode("landing")}
      />
    );
  }
  if (mode === "guide_wa") {
    return (
      <GuideWAWrapper
        lang={lang}
        user={user}
        onBackHome={() => { setMode("landing"); setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50); }}
        onOpenCV={() => { setMode("landing"); setStep(1); setTimeout(() => { const el = document.querySelector("[data-testid=hero-cta-primary]"); el?.click(); }, 100); }}
      />
    );
  }

  return (
    <div className="App min-h-screen">
      {handlingCallback && (
        <AuthCallback
          onDone={() => {
            setHandlingCallback(false);
            refreshAuth();
          }}
        />
      )}
      {!handlingCallback && (
        <>
      {mode === "landing" && <LiveCounter te={te} />}
      {mode === "landing" && <SocialProofToast lang={lang} />}
      <Header
        lang={lang}
        setLang={setLang}
        onCta={startWizard}
        onVisaDays={openVisaDays}
        user={user}
        onLogin={login}
        onLogout={logout}
        onOpenRedeem={() => setRedeemOpen(true)}
        onOpenAdmin={() => setMode("admin")}
        onOpenMyCVs={openMyCVs}
        onOpenUpgrade={() => setUpgradeOpen(true)}
        onOpenGuideWA={() => setMode("guide_wa")}
        onHome={() => {
          setMode("landing");
          setStep(1);
          setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
        }}
      />

      {mode === "landing" && (
        <main>
          <Hero t={t} onCta={startWizard} />
          <Stats te={te} />
          <GuideWACard lang={lang} onOpen={() => setMode("guide_wa")} />
          <HiredAt te={te} />
          <BeforeAfter te={te} />
          <HowItWorks t={t} />
          <Testimonials te={te} />
          <WhySection t={t} />
          <FAQ t={t} />
          <FinalCTA te={te} onCta={startWizard} />
        </main>
      )}

      {mode === "wizard" && (
        <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16" data-testid="wizard-container">
          {/* Quota banner (logged-in free users only) */}
          {user && quota && !quota.is_premium && !quota.is_admin && (
            <div
              className={`mb-6 rounded-2xl border-2 p-4 flex items-center justify-between gap-3 ${
                quota.cv_used >= quota.cv_limit
                  ? "border-[#D9423E] bg-[#FDEEEC]"
                  : "border-[#EAE5DB] bg-white"
              }`}
              data-testid="quota-banner"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#D95D39]/15 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-[#D95D39]" />
                </div>
                <div>
                  <div className="text-xs text-[#78716C]">{ql.used}</div>
                  <div className="font-heading font-extrabold text-xl text-[#292524]">
                    {quota.cv_used} <span className="text-[#78716C] text-sm font-semibold">{ql.of} {quota.cv_limit}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setRedeemOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-xl bg-[#292524] text-white hover:bg-[#1c1917] transition-colors"
                data-testid="quota-redeem-btn"
              >
                <Ticket className="w-3.5 h-3.5" /> {ql.cta}
              </button>
            </div>
          )}
          {user && quota && quota.is_premium && (
            <div className="mb-6 rounded-2xl border-2 border-[#E5A93D] bg-gradient-to-r from-[#FDF7E6] to-white p-4 flex items-center gap-3" data-testid="quota-premium-banner">
              <Crown className="w-5 h-5 text-[#E5A93D]" />
              <div className="text-sm">
                <span className="font-bold text-[#292524]">Premium unlocked</span>
                <span className="text-[#78716C]"> — unlimited CV generations 🎉</span>
              </div>
            </div>
          )}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <div className="overline">
                {t.wizard.step} {Math.min(step, 3)} {t.wizard.of} 3
              </div>
              <Languages className="w-4 h-4 text-[#78716C]" />
            </div>
            <Stepper step={Math.min(step, 3)} total={3} />
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
            >
              {step === 1 && <Step1Upload t={t} cvText={cvText} setCvText={setCvText} fileName={fileName} setFileName={setFileName} />}
              {step === 2 && <Step2Job t={t} jobText={jobText} setJobText={setJobText} jobUrl={jobUrl} setJobUrl={setJobUrl} jobMeta={jobMeta} setJobMeta={setJobMeta} lang={lang} />}
              {step === 3 && <Step3Profile t={t} profile={profile} setProfile={setProfile} />}
              {step === 4 && <Step4Processing t={t} />}
            </motion.div>
          </AnimatePresence>

          {error && (
            <div className="mt-6 text-sm text-[#D9423E] flex items-center gap-2" data-testid="wizard-error">
              <AlertTriangle className="w-4 h-4" /> {error}
            </div>
          )}

          {step < 4 && (
            <div className="flex items-center justify-between gap-3 mt-10">
              <button className="btn-outline" onClick={back} data-testid="wizard-back-btn">
                <ChevronLeft className="w-4 h-4" /> {t.wizard.back}
              </button>
              <button
                className="btn-primary"
                onClick={next}
                disabled={optimizing || !canNext()}
                data-testid="wizard-next-btn"
              >
                {step === 3 ? (
                  <>
                    <Sparkles className="w-4 h-4" /> {t.wizard.optimize}
                  </>
                ) : (
                  <>
                    {t.wizard.next} <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          )}
        </main>
      )}

      {mode === "results" && result && (
        <ResultsScreen t={t} result={result} onRestart={restart} />
      )}

      {mode === "visadays" && (
        <VisaDaysPage lang={lang} onBack={() => setMode("landing")} user={user} />
      )}

      {mode === "mycvs" && (
        <MyCVsPage
          lang={lang}
          onBack={() => setMode("landing")}
          onStartWizard={startWizard}
          onOpenRedeem={() => setRedeemOpen(true)}
        />
      )}

      {/* Floating Support Widget — visible on all pages */}
      <SupportWidget lang={lang} user={user} />
      <FloatingGuideButton lang={lang} onOpen={() => setMode("guide_wa")} />

      {/* Redeem promo code modal (global, accessible anywhere) */}
      <RedeemPromoModal
        open={redeemOpen}
        onClose={() => setRedeemOpen(false)}
        lang={lang}
        onSuccess={() => {
          refreshAuth();
          refreshQuota();
        }}
      />

      {/* Upgrade to Premium (Stripe checkout) modal */}
      <UpgradeModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        lang={lang}
        onOpenRedeem={() => setRedeemOpen(true)}
      />

      {/* Quota reached — friendly block modal */}
      <AnimatePresence>
        {quotaBlockOpen && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setQuotaBlockOpen(false)}
            data-testid="quota-block-modal"
          >
            <motion.div
              className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl text-center"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-14 h-14 rounded-2xl bg-[#D95D39]/15 mx-auto mb-4 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-[#D95D39]" />
              </div>
              <div className="font-heading font-bold text-xl text-[#292524] mb-2">{ql.upgrade}</div>
              <p className="text-sm text-[#57534E] mb-5">{ql.upgrade_sub}</p>
              <button
                onClick={() => { setQuotaBlockOpen(false); setUpgradeOpen(true); }}
                className="btn-primary w-full mb-2"
                data-testid="quota-block-upgrade"
              >
                <Crown className="w-4 h-4" /> {lang === "fr" ? "Passer Premium" : "Get Premium"}
              </button>
              <button
                onClick={() => { setQuotaBlockOpen(false); setRedeemOpen(true); }}
                className="w-full py-2 text-sm text-[#78716C] hover:text-[#D95D39] transition-colors"
                data-testid="quota-block-cta"
              >
                <Ticket className="w-3.5 h-3.5 inline mr-1" /> {ql.cta}
              </button>
              <button
                onClick={() => setQuotaBlockOpen(false)}
                className="text-xs text-[#78716C] hover:underline mt-2"
              >
                {ql.close}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer t={t} />
        </>
      )}
    </div>
  );
}

export default App;
